"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import type { NotificationDisplayItem } from "@/types/feed.types";
import type { NotificationFilter } from "../NotificationsCauseFilter";
import { useSetTitle } from "@/contexts/TitleContext";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { useNotificationsQuery } from "@/hooks/useNotificationsQuery";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import { useLayout } from "../../my-stream/layout/LayoutContext";
import { commonApiPostWithoutBodyAndResponse } from "@/services/api/common-api";
import { DEFAULT_ERROR_MESSAGE, LOAD_TIMEOUT_MESSAGE, LOAD_TIMEOUT_MS } from "../utils/constants";
import { getNotificationErrorDetails } from "../utils/getNotificationErrorDetails";
interface NotificationsContentState {
  readonly isLoadingProfile: boolean;
  readonly hasConnectedProfile: boolean;
  readonly hasProfileHandle: boolean;
  readonly showProxyDisabledState: boolean;
  readonly showErrorState: boolean;
  readonly resolvedErrorMessage: string;
  readonly showLoader: boolean;
  readonly showNoItems: boolean;
}
interface NotificationsHandlers {
  readonly handleRetry: () => void;
  readonly handleAuthRetry: () => void;
  readonly handleProxyDisable: () => void;
}
interface NotificationsPagination {
  readonly hasNextPage: boolean;
  readonly fetchNextPage: () => void;
}
interface UseNotificationsControllerResult {
  readonly activeFilter: NotificationFilter | null;
  readonly setActiveFilter: (filter: NotificationFilter | null) => void;
  readonly isAuthenticated: boolean;
  readonly notificationsViewStyle: CSSProperties;
  readonly items: NotificationDisplayItem[];
  readonly isFetchingNextPage: boolean;
  readonly pagination: NotificationsPagination;
  readonly contentState: NotificationsContentState;
  readonly handlers: NotificationsHandlers;
  readonly markNotificationIdsAsRead: (ids: number[]) => Promise<void>;
}
export const useNotificationsController = () => {
  const $ = _c(91);
  const {
    connectedProfile,
    activeProfileProxy,
    fetchingProfile,
    requestAuth,
    setToast,
    setActiveProfileProxy
  } = useContext(AuthContext);
  const {
    notificationsViewStyle
  } = useLayout();
  const {
    removeAllDeliveredNotifications
  } = useNotificationsContext();
  const {
    invalidateNotifications
  } = useContext(ReactQueryWrapperContext);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hasMarkedAllAsReadRef = useRef(false);
  const errorToastShownRef = useRef(false);
  const reauthTriggeredRef = useRef(false);
  const timeoutToastShownRef = useRef(false);
  const lastErrorMessageRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams?.get("reload") ?? undefined;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const reload = t0;
  const isAuthenticated = !!connectedProfile?.handle && !activeProfileProxy;
  const isLoadingProfile = fetchingProfile && !connectedProfile;
  const hasConnectedProfile = !!connectedProfile;
  const hasProfileHandle = !!connectedProfile?.handle;
  useSetTitle("Notifications | My Stream | Brain");
  let t1;
  if ($[2] !== invalidateNotifications || $[3] !== removeAllDeliveredNotifications) {
    t1 = async () => {
      ;
      try {
        invalidateNotifications();
        await removeAllDeliveredNotifications();
      } catch (t2) {
        const error = t2;
        console.error("Failed to clear delivered notifications:", error);
      }
    };
    $[2] = invalidateNotifications;
    $[3] = removeAllDeliveredNotifications;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== setToast) {
    t2 = error_0 => {
      setToast({
        message: error_0 instanceof Error ? error_0.message : String(error_0),
        type: "error"
      });
    };
    $[5] = setToast;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      mutationFn: _temp,
      onSuccess: t1,
      onError: t2
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    mutateAsync: markAllAsRead
  } = useMutation(t3);
  let t4;
  let t5;
  if ($[10] !== isAuthenticated || $[11] !== markAllAsRead || $[12] !== reload) {
    t4 = () => {
      if (!isAuthenticated) {
        return;
      }
      if (reload === "true" || hasMarkedAllAsReadRef.current) {
        return;
      }
      hasMarkedAllAsReadRef.current = true;
      const id = setTimeout(() => {
        markAllAsRead().catch(_temp2);
      }, 0);
      return () => clearTimeout(id);
    };
    t5 = [isAuthenticated, markAllAsRead, reload];
    $[10] = isAuthenticated;
    $[11] = markAllAsRead;
    $[12] = reload;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[15] !== isAuthenticated) {
    t6 = () => {
      if (!isAuthenticated) {
        hasMarkedAllAsReadRef.current = false;
      }
    };
    t7 = [isAuthenticated];
    $[15] = isAuthenticated;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useEffect(t6, t7);
  const t8 = isAuthenticated ? connectedProfile?.handle : undefined;
  const t9 = !!activeProfileProxy;
  const t10 = activeFilter?.cause?.length ? activeFilter.cause : null;
  let t11;
  if ($[18] !== t10 || $[19] !== t8 || $[20] !== t9) {
    t11 = {
      identity: t8,
      activeProfileProxy: t9,
      limit: "30",
      reverse: true,
      cause: t10
    };
    $[18] = t10;
    $[19] = t8;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const {
    items,
    rawItems: rawItemsFromQuery,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    isInitialQueryDone,
    isSuccess,
    error: queryError
  } = useNotificationsQuery(t11);
  const rawItems = rawItemsFromQuery ?? items;
  let t12;
  if ($[22] !== invalidateNotifications || $[23] !== removeAllDeliveredNotifications) {
    t12 = async () => {
      ;
      try {
        invalidateNotifications();
        await removeAllDeliveredNotifications();
      } catch (t13) {
        const error_2 = t13;
        console.error("Failed to clear delivered notifications:", error_2);
      }
    };
    $[22] = invalidateNotifications;
    $[23] = removeAllDeliveredNotifications;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== setToast) {
    t13 = error_3 => {
      setToast({
        message: error_3 instanceof Error ? error_3.message : String(error_3),
        type: "error"
      });
    };
    $[25] = setToast;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t12 || $[28] !== t13) {
    t14 = {
      mutationFn: _temp4,
      onSuccess: t12,
      onError: t13
    };
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const {
    mutateAsync: markNotificationIdsAsRead
  } = useMutation(t14);
  let t15;
  let t16;
  if ($[30] !== isAuthenticated || $[31] !== markAllAsRead || $[32] !== pathname || $[33] !== refetch || $[34] !== reload || $[35] !== router || $[36] !== searchParams) {
    t15 = () => {
      if (reload !== "true") {
        return;
      }
      const clearReloadParam = () => {
        const params = new URLSearchParams(searchParams?.toString() || "");
        params.delete("reload");
        const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname || "/notifications";
        router.replace(newUrl, {
          scroll: false
        });
      };
      if (!isAuthenticated) {
        clearReloadParam();
        return;
      }
      refetch().then(() => {
        hasMarkedAllAsReadRef.current = true;
        return markAllAsRead();
      }).catch(_temp5).finally(() => {
        clearReloadParam();
      });
    };
    t16 = [pathname, isAuthenticated, markAllAsRead, refetch, reload, router, searchParams];
    $[30] = isAuthenticated;
    $[31] = markAllAsRead;
    $[32] = pathname;
    $[33] = refetch;
    $[34] = reload;
    $[35] = router;
    $[36] = searchParams;
    $[37] = t15;
    $[38] = t16;
  } else {
    t15 = $[37];
    t16 = $[38];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[39] !== queryError || $[40] !== requestAuth || $[41] !== setToast) {
    t17 = () => {
      if (!queryError) {
        setErrorMessage(null);
        setHasTimedOut(false);
        errorToastShownRef.current = false;
        reauthTriggeredRef.current = false;
        lastErrorMessageRef.current = null;
        return;
      }
      const {
        message,
        isUnauthorized
      } = getNotificationErrorDetails(queryError);
      if (lastErrorMessageRef.current !== message) {
        errorToastShownRef.current = false;
        reauthTriggeredRef.current = false;
        lastErrorMessageRef.current = message;
      }
      setErrorMessage(message);
      setHasTimedOut(false);
      if (!errorToastShownRef.current) {
        setToast({
          message,
          type: "error"
        });
        errorToastShownRef.current = true;
      }
      if (isUnauthorized && !reauthTriggeredRef.current) {
        requestAuth().catch(_temp6);
        reauthTriggeredRef.current = true;
      }
    };
    t18 = [queryError, requestAuth, setToast];
    $[39] = queryError;
    $[40] = requestAuth;
    $[41] = setToast;
    $[42] = t17;
    $[43] = t18;
  } else {
    t17 = $[42];
    t18 = $[43];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[44] !== errorMessage || $[45] !== isAuthenticated || $[46] !== isInitialQueryDone || $[47] !== isLoadingProfile || $[48] !== isSuccess) {
    t19 = () => {
      if (isSuccess) {
        setHasTimedOut(false);
        timeoutToastShownRef.current = false;
        return;
      }
      if (errorMessage || !isAuthenticated || isLoadingProfile) {
        setHasTimedOut(false);
        timeoutToastShownRef.current = false;
        return;
      }
      if (isInitialQueryDone) {
        return;
      }
      const timerId = globalThis.setTimeout(() => {
        setHasTimedOut(true);
      }, LOAD_TIMEOUT_MS);
      return () => {
        globalThis.clearTimeout(timerId);
      };
    };
    t20 = [errorMessage, isAuthenticated, isInitialQueryDone, isLoadingProfile, isSuccess];
    $[44] = errorMessage;
    $[45] = isAuthenticated;
    $[46] = isInitialQueryDone;
    $[47] = isLoadingProfile;
    $[48] = isSuccess;
    $[49] = t19;
    $[50] = t20;
  } else {
    t19 = $[49];
    t20 = $[50];
  }
  useEffect(t19, t20);
  let t21;
  let t22;
  if ($[51] !== hasTimedOut || $[52] !== setToast) {
    t21 = () => {
      if (hasTimedOut) {
        if (!timeoutToastShownRef.current) {
          setToast({
            message: LOAD_TIMEOUT_MESSAGE,
            type: "warning"
          });
          timeoutToastShownRef.current = true;
        }
      } else {
        timeoutToastShownRef.current = false;
      }
    };
    t22 = [hasTimedOut, setToast];
    $[51] = hasTimedOut;
    $[52] = setToast;
    $[53] = t21;
    $[54] = t22;
  } else {
    t21 = $[53];
    t22 = $[54];
  }
  useEffect(t21, t22);
  let t23;
  if ($[55] !== refetch) {
    t23 = () => {
      setHasTimedOut(false);
      setErrorMessage(null);
      errorToastShownRef.current = false;
      reauthTriggeredRef.current = false;
      lastErrorMessageRef.current = null;
      refetch({
        cancelRefetch: true
      }).catch(_temp7);
    };
    $[55] = refetch;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  const handleRetry = t23;
  let t24;
  if ($[57] !== requestAuth || $[58] !== setToast) {
    t24 = () => {
      requestAuth().catch(error_7 => {
        console.error("Failed to re-authenticate:", error_7);
        setToast({
          message: error_7 instanceof Error ? error_7.message : DEFAULT_ERROR_MESSAGE,
          type: "error"
        });
      });
    };
    $[57] = requestAuth;
    $[58] = setToast;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  const handleAuthRetry = t24;
  let t25;
  if ($[60] !== setActiveProfileProxy || $[61] !== setToast) {
    t25 = () => {
      setActiveProfileProxy(null).catch(error_8 => {
        console.error("Failed to switch to primary profile:", error_8);
        setToast({
          message: error_8 instanceof Error ? error_8.message : "Unable to switch to primary profile. Please try again.",
          type: "error"
        });
      });
    };
    $[60] = setActiveProfileProxy;
    $[61] = setToast;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  const handleProxyDisable = t25;
  const showLoader = isAuthenticated && !hasTimedOut && !errorMessage && (!isInitialQueryDone || isFetching) && rawItems.length === 0;
  const showNoItems = isAuthenticated && !errorMessage && !hasTimedOut && isInitialQueryDone && !isFetching && rawItems.length === 0;
  const showErrorState = (!!errorMessage || hasTimedOut) && rawItems.length === 0;
  const showProxyDisabledState = !!activeProfileProxy;
  const resolvedErrorMessage = hasTimedOut ? LOAD_TIMEOUT_MESSAGE : errorMessage ?? DEFAULT_ERROR_MESSAGE;
  let t26;
  if ($[63] !== hasConnectedProfile || $[64] !== hasProfileHandle || $[65] !== isLoadingProfile || $[66] !== resolvedErrorMessage || $[67] !== showErrorState || $[68] !== showLoader || $[69] !== showNoItems || $[70] !== showProxyDisabledState) {
    t26 = {
      isLoadingProfile,
      hasConnectedProfile,
      hasProfileHandle,
      showProxyDisabledState,
      showErrorState,
      resolvedErrorMessage,
      showLoader,
      showNoItems
    };
    $[63] = hasConnectedProfile;
    $[64] = hasProfileHandle;
    $[65] = isLoadingProfile;
    $[66] = resolvedErrorMessage;
    $[67] = showErrorState;
    $[68] = showLoader;
    $[69] = showNoItems;
    $[70] = showProxyDisabledState;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  const contentState = t26;
  let t27;
  if ($[72] !== handleAuthRetry || $[73] !== handleProxyDisable || $[74] !== handleRetry) {
    t27 = {
      handleRetry,
      handleAuthRetry,
      handleProxyDisable
    };
    $[72] = handleAuthRetry;
    $[73] = handleProxyDisable;
    $[74] = handleRetry;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  const handlers = t27;
  const t28 = !!hasNextPage;
  let t29;
  if ($[76] !== fetchNextPage || $[77] !== t28) {
    t29 = {
      hasNextPage: t28,
      fetchNextPage
    };
    $[76] = fetchNextPage;
    $[77] = t28;
    $[78] = t29;
  } else {
    t29 = $[78];
  }
  const pagination = t29;
  let t30;
  if ($[79] !== markNotificationIdsAsRead) {
    t30 = ids_0 => markNotificationIdsAsRead(ids_0);
    $[79] = markNotificationIdsAsRead;
    $[80] = t30;
  } else {
    t30 = $[80];
  }
  let t31;
  if ($[81] !== activeFilter || $[82] !== contentState || $[83] !== handlers || $[84] !== isAuthenticated || $[85] !== isFetchingNextPage || $[86] !== items || $[87] !== notificationsViewStyle || $[88] !== pagination || $[89] !== t30) {
    t31 = {
      activeFilter,
      setActiveFilter,
      isAuthenticated,
      notificationsViewStyle,
      items,
      isFetchingNextPage,
      pagination,
      contentState,
      handlers,
      markNotificationIdsAsRead: t30
    };
    $[81] = activeFilter;
    $[82] = contentState;
    $[83] = handlers;
    $[84] = isAuthenticated;
    $[85] = isFetchingNextPage;
    $[86] = items;
    $[87] = notificationsViewStyle;
    $[88] = pagination;
    $[89] = t30;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  return t31;
};
async function _temp() {
  return await commonApiPostWithoutBodyAndResponse({
    endpoint: "notifications/read"
  });
}
function _temp2(error_1) {
  console.error("Failed to mark notifications as read:", error_1);
}
function _temp3(id_0) {
  return commonApiPostWithoutBodyAndResponse({
    endpoint: `notifications/${id_0}/read`
  });
}
async function _temp4(ids) {
  await Promise.all(ids.map(_temp3));
}
function _temp5(error_4) {
  console.error("Error during refetch:", error_4);
}
function _temp6(error_5) {
  console.error("Failed to re-authenticate after notifications error:", error_5);
}
function _temp7(error_6) {
  console.error("Failed to retry notifications fetch:", error_6);
}